import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { RECOVERY_AUDIT_PACKET } from "@/data/mock";

/* RCV-05 — Post-Recovery Audit Packet.
   Canonical source: prototype/pages/recovery/audit.html (no current
   Figma frame located). Unlike SVC-03/04's "(production only)"
   deferrals, this screen's "Download PDF" / "Send to Support" actions
   have literal, non-deferred success toasts in the canonical source —
   reproduced verbatim here, since the source itself does not claim
   real backend persistence beyond the toast. */

export function RecoveryAudit() {
  const p = RECOVERY_AUDIT_PACKET;

  function downloadPdf() {
    toast({ variant: "success", title: "Audit packet downloaded (PDF, 1.2 MB)", description: LOCAL_ONLY_NOTICE });
  }

  function sendToSupport() {
    toast({ variant: "success", title: "Packet attached to support ticket #4421", description: LOCAL_ONLY_NOTICE });
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Recovery Audit Packet — {p.id}</h1>
          <p className="text-sm text-muted">Created {p.createdAt}</p>
        </div>
        <div className="flex gap-2.5">
          <Button variant="secondary" onClick={downloadPdf}>⬇ Download PDF</Button>
          <Button variant="secondary" onClick={sendToSupport}>Send to Support</Button>
        </div>
      </div>

      <div className="mb-5 rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 text-base font-bold text-ink">Packet Details</div>
        <dl className="space-y-2.5 text-sm">
          <Row k="Requested By" v={p.requestedBy} />
          <Row k="Justification" v={p.justification} />
          <Row k="Approval" v={p.approval} />
          <Row k="Execution Window" v={p.executionWindow} />
          <Row k="Quorum" v={p.quorum} />
          <Row k="Key Fingerprint" v={p.keyFingerprint} />
          <Row k="Result" v={p.result} />
        </dl>
      </div>

      <div className="mb-5 overflow-hidden rounded-[10px] border border-line bg-console-bg">
        <div className="border-b border-line bg-console-panel px-4 py-2 font-mono text-xs text-console-muted">Evidence Chain</div>
        <div className="space-y-2 p-4 font-mono text-[13px] leading-relaxed text-console-text">
          <div><span className="text-console-muted">merkle_root:</span> {p.merkleRoot}</div>
          <div className="break-all"><span className="text-console-muted">block_refs:</span> {p.blockRefs}</div>
        </div>
      </div>

      <div className="rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 text-base font-bold text-ink">Custodian Signatures</div>
        <ul className="divide-y divide-line">
          {p.signatures.map((s) => (
            <li key={s.name} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
              <span className="font-medium text-ink">{s.name}</span>
              <span className="font-mono text-xs text-muted">{s.sig} · {s.method} · {s.timestamp}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">{v}</dd>
    </div>
  );
}
